import { FC } from "react";
import { useFormikContext } from "formik";
import FormSection from "@blanket/components/formSections/FormSection";
import SubGroupPanel from "@blanket/components/formSections/SubGroupPanel";
import type { CompanyRegistrationValues } from "../model/registrationTypes";
import { JaNej } from "../model/registrationEnums";
import { EmployeesFieldArray } from "../components/employees/EmployeesFieldArray";
import { optionalMobilnummerField } from "../components/employees/employeePhoneFieldConfig";
import {
  SelectFormField,
  TextFormField,
  YesNoFormField,
} from "@blanket/components/formFields/FormFields";
import { useCompanyNameLookup } from "@blanket/hooks/useCompanyNameLookup";
import { CompanyNameSuggestions } from "../components/CompanyNameSuggestions";
import { RegistrationDateDisclosureNotice } from "../components/RegistrationDateDisclosureNotice";
import { CompanyContactDetails } from "../components/CompanyContactDetails";
import { InvoiceDispatchFields } from "../components/InvoiceDispatchFields";
import { RegistrationRemarksField } from "../components/RegistrationRemarksField";
import { RegistrationContactFields } from "../components/RegistrationContactFields";

export const EnkeltmandsvirksomhedSection: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const enkeltmandsvirksomhed = values.enkeltmandsvirksomhed;
  const nameLookup = useCompanyNameLookup({
    name: "enkeltmandsvirksomhed.name",
    cvr: "enkeltmandsvirksomhed.cvr",
    pNumber: "enkeltmandsvirksomhed.pNumber",
  });

  return (
    <>
      <FormSection
        id="section-enkeltmandsvirksomhed-info"
        number={2}
        title="Stamoplysninger om virksomheden"
        description="Stamoplysninger og forsikring."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyNameSuggestions
            items={nameLookup.data}
            isLoading={nameLookup.isLoading}
            onSelect={nameLookup.select}
            onDismiss={nameLookup.dismiss}
          >
            <TextFormField
              name="enkeltmandsvirksomhed.name"
              label="Virksomhedens navn"
              isRequired
              helperText='Vær opmærksom på, at branchekoden i CVR skal være "691000 Juridiske aktiviteter".'
              id="enkeltmandsvirksomhed-input-name"
            />
          </CompanyNameSuggestions>
          <div className="blanket-two-col">
            <TextFormField
              name="enkeltmandsvirksomhed.cvr"
              label="CVR-nr."
              id="enkeltmandsvirksomhed-input-cvr"
              isRequired
              type="number"
            />
            {nameLookup.availablePNumbers.length > 1 ? (
              <SelectFormField
                key="enkeltmandsvirksomhed-p-number-select"
                name="enkeltmandsvirksomhed.pNumber"
                label="P-nummer"
                id="enkeltmandsvirksomhed-input-p-number"
                isRequired
                placeholder="Vælg P-nummer"
                helperText="Vær opmærksom på, at der skal indsendes en blanket for hvert p-nummer, der oprettes. Det skyldes bl.a., at medarbejdere skal registreres under det korrekte p-nummer."
                options={nameLookup.availablePNumbers.map((p) => ({
                  value: String(p),
                  label: String(p),
                }))}
              />
            ) : (
              <TextFormField
                key="enkeltmandsvirksomhed-p-number-text"
                name="enkeltmandsvirksomhed.pNumber"
                label="P-nummer"
                id="enkeltmandsvirksomhed-input-p-number"
                isRequired
                type="number"
                helperText="Vær opmærksom på, at der skal indsendes en blanket for hvert p-nummer, der oprettes. Det skyldes bl.a., at medarbejdere skal registreres under det korrekte p-nummer."
              />
            )}
          </div>
          <div className="d-flex flex-column gap-1">
            <div className="blanket-two-col">
              <TextFormField
                name="enkeltmandsvirksomhed.insuranceCompany"
                label="Navn på forsikringsselskab"
                id="enkeltmandsvirksomhed-input-insurance-company"
                type="text"
              />
              <TextFormField
                name="enkeltmandsvirksomhed.insurancePolicyNumber"
                label="Policenummer"
                id="enkeltmandsvirksomhed-input-insurance-policy-number"
                type="text"
              />
            </div>
            <div className="form-text mb-0">
              <p className="mb-2">
                Hvis du endnu ikke kender forsikringsselskabet og/eller
                policenummeret, skal dette eftersendes til Advokatsamfundet
                (forsikringer@advokatsamfundet.dk).
              </p>
              <p className="mb-0">
                Vær opmærksom på, at forsikringen skal være gældende fra
                opstartsdatoen.
              </p>
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection
        id="section-enkeltmandsvirksomhed-contact"
        number={3}
        title="Kontaktoplysninger til virksomheden"
        description="Telefon, e-mail og fakturering."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyContactDetails
            namePrefix="enkeltmandsvirksomhed"
            idPrefix="enkeltmandsvirksomhed"
            emailLabel="E-mailadresse"
          />
          <InvoiceDispatchFields
            namePrefix="enkeltmandsvirksomhed"
            idPrefix="enkeltmandsvirksomhed"
            invoiceTarget={enkeltmandsvirksomhed.invoiceTarget}
          />
          <TextFormField
            name="enkeltmandsvirksomhed.website"
            label="Virksomhedens hjemmeside"
            id="enkeltmandsvirksomhed-input-website"
            type="url"
          />
        </div>
      </FormSection>

      <FormSection
        id="section-enkeltmandsvirksomhed-details"
        number={4}
        title="Oplysninger om virksomheden"
        description="Datoer, tidligere tilknytning og forsikring."
      >
        <div className="d-flex flex-column gap-4">
          <YesNoFormField
            name="enkeltmandsvirksomhed.startFromOtherCompany"
            label="Ophører din tilknytning til en anden advokatvirksomhed i forbindelse med opstarten af denne virksomhed?"
            id="enkeltmandsvirksomhed-input-start-from-other-company"
            isRequired
          />
          {enkeltmandsvirksomhed.startFromOtherCompany === JaNej.Ja && (
            <SubGroupPanel
              title="Tidligere virksomhed"
              description="Angiv navn og ophørsdato på virksomheden, du ophører i."
            >
              <div className="blanket-two-col">
                <TextFormField
                  name="enkeltmandsvirksomhed.otherCompanyName"
                  label="Navn på virksomhed, du ophører i"
                  id="enkeltmandsvirksomhed-input-other-company-name"
                  isRequired
                />
                <TextFormField
                  name="enkeltmandsvirksomhed.otherCompanyEndDate"
                  label="Ophørsdato"
                  id="enkeltmandsvirksomhed-input-other-company-end-date"
                  isRequired
                  type="date"
                />
              </div>
            </SubGroupPanel>
          )}
          <EmployeesFieldArray
            namePrefix="enkeltmandsvirksomhed"
            phoneField={optionalMobilnummerField}
            employeesQuestionLabel="Er der andre ansatte i virksomheden?"
          />
        </div>
      </FormSection>
      <FormSection
        id="section-enkeltmandsvirksomhed-dates"
        number={5}
        title="Oprettelses- og driftdato"
      >
        <div className="mb-4">
          <RegistrationRemarksField
            namePrefix="enkeltmandsvirksomhed"
            idPrefix="enkeltmandsvirksomhed"
          />
        </div>
        <div className="blanket-two-col">
          <TextFormField
            name="enkeltmandsvirksomhed.cvrCreationDate"
            label="Oprettelsesdato i CVR"
            isRequired
            helperText="Hvis oprettelsesdato og dato for opstart af drift er den samme, indtastes dette."
            id="enkeltmandsvirksomhed-input-cvr-creation-date"
            type="date"
          />
          <TextFormField
            name="enkeltmandsvirksomhed.startDate"
            label="Dato for opstart af drift"
            isRequired
            helperText="Hvis oprettelsesdato og dato for opstart af drift er den samme, indtastes dette."
            id="enkeltmandsvirksomhed-input-start-date"
            type="date"
          />
        </div>
        <RegistrationDateDisclosureNotice />
        <div className="blanket-two-col mt-4">
          <RegistrationContactFields
            namePrefix="enkeltmandsvirksomhed"
            idPrefix="enkeltmandsvirksomhed"
          />
        </div>
      </FormSection>
    </>
  );
};
